import {useIssueState} from '@github-ui/issue-pr-state'
import type {IssueStateReasonType, PullRequestStateType} from '@github-ui/issue-pr-state/constants'
import {Link} from '@github-ui/react-router'
import type {CrossReferenceInfo} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {formatIssueReference} from '@github-ui/timeline-items/FormatIssueReference'
import {useCrossRepositoryLinkClick} from '../../../../hooks/use-cross-repository-link-navigation'
import {useLayoutBaseRepository} from '../../../../stores/layout-selectors'

const PULL_REQUEST_STATES = ['OPEN', 'CLOSED', 'MERGED', 'IN_MERGE_QUEUE', 'DRAFT'] as const
const ISSUE_STATE_REASONS = ['COMPLETED', 'DUPLICATE', 'NOT_PLANNED', 'REOPENED'] as const

function includesValue<T extends string>(values: readonly T[], value: string | undefined): value is T {
  return value !== undefined && values.includes(value as T)
}

function normalizeSourceState(value: string | undefined): PullRequestStateType {
  return includesValue(PULL_REQUEST_STATES, value) ? value : undefined
}

function normalizeSourceStateReason(value: string | undefined): IssueStateReasonType {
  return includesValue(ISSUE_STATE_REASONS, value) ? value : undefined
}

export function CrossReferenceText({info}: {info: CrossReferenceInfo}) {
  const {
    sourceType,
    sourceUrl,
    sourceTitle,
    sourceNumber,
    sourceRepositoryNameWithOwner,
    sourceState,
    sourceStateReason,
    sourceIsDraft,
    sourceIsInMergeQueue,
  } = info
  const subjectType = sourceType === 'PullRequest' ? 'PullRequest' : 'Issue'
  const {sourceIcon} = useIssueState({
    state: normalizeSourceState(sourceState),
    stateReason: normalizeSourceStateReason(sourceStateReason),
  })
  const SourceIcon = sourceIcon(subjectType, sourceIsDraft, sourceIsInMergeQueue)
  const baseRepository = useLayoutBaseRepository()
  const handleLinkClick = useCrossRepositoryLinkClick()

  if (!sourceUrl || !sourceTitle) {
    return <>mentioned this</>
  }

  const [sourceOwnerLogin, sourceRepoName] = sourceRepositoryNameWithOwner?.split('/') ?? []
  const targetRepositoryNameWithOwner = `${baseRepository.ownerLogin}/${baseRepository.name}`
  const reference =
    sourceNumber !== undefined && sourceOwnerLogin && sourceRepoName
      ? formatIssueReference(
          {
            number: sourceNumber,
            ownerLogin: sourceOwnerLogin,
            repoId: `${sourceOwnerLogin}/${sourceRepoName}`,
            repoName: sourceRepoName,
          },
          targetRepositoryNameWithOwner,
          baseRepository.ownerLogin,
        )
      : undefined

  return (
    <>
      mentioned this in{' '}
      <Link
        className="text-underline color-fg-default"
        to={sourceUrl}
        onClick={event => handleLinkClick(event, event.currentTarget)}
      >
        {/* eslint-disable-next-line react-hooks/static-components -- selected from the shared state-icon map. */}
        <SourceIcon aria-hidden size={16} className="mr-1" />
        <span className="text-semibold">{sourceTitle}</span>
        {reference ? <span className="color-fg-muted"> {reference}</span> : null}
      </Link>
    </>
  )
}
